import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import { Navigate } from 'react-router-dom';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './BotSchedulesPage.css';

// Constants
const DAYS = [
  { index: 0, label: 'Mandag', short: 'Man' },
  { index: 1, label: 'Tirsdag', short: 'Tir' },
  { index: 2, label: 'Onsdag', short: 'Ons' },
  { index: 3, label: 'Torsdag', short: 'Tor' },
  { index: 4, label: 'Fredag', short: 'Fre' },
  { index: 5, label: 'Lørdag', short: 'Lør' },
  { index: 6, label: 'Søndag', short: 'Søn' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const SLOT_MODES = {
  ai_only: {
    label: 'Kun AI',
    shortLabel: 'AI',
    color: '#1d4ed8',
    bgColor: '#93c5fd',
    description: 'AI håndterer alt, ingen eskalering',
  },
  ai_with_escalation: {
    label: 'AI + Eskalering',
    shortLabel: 'AI+E',
    color: '#c2410c',
    bgColor: '#fed7aa',
    description: 'AI først, kan eskalere til agent',
  },
  live_only: {
    label: 'Kun Live',
    shortLabel: 'Live',
    color: '#047857',
    bgColor: '#6ee7b7',
    description: 'Direkte til live-agent',
  },
};

const DEFAULT_ESCALATION_POLICY = {
  allowQueueOutsideHours: false,
  queueMessage: 'Våre agenter er ikke tilgjengelige akkurat nå, men meldingen din vil bli behandlet så snart vi er tilbake.',
  maxQueueTimeHours: 24,
};

const DEFAULT_ESCALATION_CONFIG = {
  maxResolutionAttempts: 2,
  autoEscalateOnFrustration: true,
  autoEscalateOnExplicitRequest: true,
  salesEscalationBypassEnabled: false,
  escalationCooldownMinutes: 5,
  showEscalationOption: true,
};

// Helper: Create empty week schedule (168 slots, all ai_only)
const createEmptySchedule = () => Array(168).fill('ai_only');

// Helper: Get slot index from day and hour
const getSlotIndex = (dayIndex, hour) => dayIndex * 24 + hour;

// ============================================
// MODE SELECTION CARDS
// ============================================
const ModeCard = ({ mode, selected, onClick }) => {
  const configs = {
    ai_only: {
      icon: '🤖',
      title: 'Kun AI-agent',
      description: 'AI-agenten håndterer alle henvendelser automatisk, 24/7. Ingen eskalering til mennesker.',
      benefits: ['Alltid tilgjengelig', 'Ingen ventetid', 'Konsistente svar'],
    },
    live_only: {
      icon: '👤',
      title: 'Kun live-agenter',
      description: 'Alle henvendelser går direkte til menneskelige agenter. AI tar over utenfor åpningstid.',
      benefits: ['Personlig kontakt', 'Menneskelig vurdering', 'AI-backup'],
    },
    combined: {
      icon: '🤝',
      title: 'Kombinert',
      description: 'Konfigurer nøyaktig når hver modus er aktiv med den interaktive ukeoversikten.',
      benefits: ['Full kontroll', 'Fleksibel tidsplan', 'Smart eskalering'],
    },
  };

  const config = configs[mode];

  return (
    <button
      type="button"
      className={`mode-card ${selected ? 'selected' : ''}`}
      onClick={() => onClick(mode)}
      data-mode={mode}
    >
      <div className="mode-card-icon">{config.icon}</div>
      <div className="mode-card-body">
        <h3>{config.title}</h3>
        <p>{config.description}</p>
        <ul className="mode-benefits">
          {config.benefits.map((b, i) => (
            <li key={i}>✓ {b}</li>
          ))}
        </ul>
      </div>
      {selected && <span className="mode-badge">Aktiv</span>}
    </button>
  );
};

// ============================================
// INTERACTIVE WEEK CALENDAR
// ============================================
const WeekCalendar = ({ schedule, onChange, onSave, currentSlot }) => {
  const [paintMode, setPaintMode] = useState('ai_with_escalation');
  const [isPainting, setIsPainting] = useState(false);

  // Handle mouse events for painting
  const handleMouseDown = (slotIndex) => {
    setIsPainting(true);
    
    // Toggle or set the mode
    const newSchedule = [...schedule];
    newSchedule[slotIndex] = paintMode;
    onChange(newSchedule);
  };

  const handleMouseEnter = (slotIndex) => {
    if (isPainting) {
      const newSchedule = [...schedule];
      newSchedule[slotIndex] = paintMode;
      onChange(newSchedule);
    }
  };

  const handleMouseUp = () => {
    if (isPainting) {
      setIsPainting(false);
      // Save when user stops painting
      if (onSave) onSave();
    }
  };

  // Add global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isPainting) {
        setIsPainting(false);
        if (onSave) onSave();
      }
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isPainting, onSave]);

  // Quick actions
  const fillWorkHours = (mode) => {
    const newSchedule = [...schedule];
    DAYS.forEach((day) => {
      if (day.index < 5) { // Mon-Fri
        for (let h = 9; h < 17; h++) {
          newSchedule[getSlotIndex(day.index, h)] = mode;
        }
      }
    });
    onChange(newSchedule);
    if (onSave) onSave();
  };

  const fillAllHours = (mode) => {
    onChange(Array(168).fill(mode));
    if (onSave) onSave();
  };

  const fillDay = (dayIndex, mode) => {
    const newSchedule = [...schedule];
    for (let h = 0; h < 24; h++) {
      newSchedule[getSlotIndex(dayIndex, h)] = mode;
    }
    onChange(newSchedule);
    if (onSave) onSave();
  };

  return (
    <div className="week-calendar" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      {/* Paint Mode Selector */}
      <div className="paint-toolbar">
        <span className="toolbar-label">Velg modus å male:</span>
        <div className="paint-modes">
          {Object.entries(SLOT_MODES).map(([mode, config]) => (
            <button
              key={mode}
              type="button"
              className={`paint-mode-btn ${paintMode === mode ? 'active' : ''}`}
              onClick={() => setPaintMode(mode)}
              style={{
                '--mode-color': config.color,
                '--mode-bg': config.bgColor,
              }}
            >
              <span className="mode-dot" />
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <span className="toolbar-label">Hurtigvalg:</span>
        <button type="button" onClick={() => fillWorkHours('ai_with_escalation')}>
          📅 Arbeidstid (AI + Eskalering)
        </button>
        <button type="button" onClick={() => fillWorkHours('live_only')}>
          👤 Arbeidstid (Kun Live)
        </button>
        <button type="button" onClick={() => fillAllHours('ai_only')}>
          🤖 Alt AI
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-container">
        {/* Hour Labels */}
        <div className="hour-labels">
          {HOURS.map((hour) => (
            <div key={hour} className="hour-label">
              {hour.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Day Rows */}
        <div className="calendar-grid">
          {DAYS.map((day) => (
            <div key={day.index} className="day-row">
              <div className="day-label">
                <span className="day-name">{day.short}</span>
                <button
                  type="button"
                  className="fill-day-btn"
                  onClick={() => fillDay(day.index, paintMode)}
                  title={`Fyll ${day.label} med ${SLOT_MODES[paintMode].label}`}
                >
                  ↻
                </button>
              </div>
              <div className="hour-slots">
                {HOURS.map((hour) => {
                  const slotIndex = getSlotIndex(day.index, hour);
                  const mode = schedule[slotIndex] || 'ai_only';
                  const modeConfig = SLOT_MODES[mode];
                  const isCurrent = slotIndex === currentSlot;

                  return (
                    <div
                      key={hour}
                      className={`hour-slot ${isCurrent ? 'current' : ''}`}
                      style={{
                        backgroundColor: modeConfig.bgColor,
                        borderColor: modeConfig.color,
                      }}
                      onMouseDown={() => handleMouseDown(slotIndex)}
                      onMouseEnter={() => handleMouseEnter(slotIndex)}
                      title={`${day.label} ${hour}:00 - ${mode === 'ai_only' ? 'Kun AI' : mode === 'live_only' ? 'Kun Live' : 'AI + Eskalering'}`}
                    >
                      {isCurrent && <span className="current-indicator">●</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        {Object.entries(SLOT_MODES).map(([mode, config]) => (
          <div key={mode} className="legend-item">
            <span
              className="legend-color"
              style={{ backgroundColor: config.bgColor, borderColor: config.color }}
            />
            <span className="legend-label">{config.label}</span>
            <span className="legend-desc">{config.description}</span>
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="calendar-instructions">
        <p>💡 <strong>Tips:</strong> Klikk og dra for å male flere timer. Bruk hurtigvalgene for vanlige mønstre.</p>
      </div>
    </div>
  );
};

// ============================================
// ESCALATION POLICY SETTINGS
// ============================================
const EscalationPolicyPanel = ({ policy, onChange, escalationConfig, onEscalationConfigChange }) => {
  const handleChange = (key, value) => {
    onChange({ ...policy, [key]: value });
  };

  const handleConfigChange = (key, value) => {
    onEscalationConfigChange({ ...escalationConfig, [key]: value });
  };

  return (
    <div className="escalation-policy-panel">
      <h4>⚙️ Eskaleringsregler</h4>
      <p className="panel-description">
        Konfigurer hvordan eskalering skal fungere utenfor definerte eskaleringsvinduer.
      </p>

      <div className="policy-option">
        <label className="policy-toggle">
          <input
            type="checkbox"
            checked={policy.allowQueueOutsideHours}
            onChange={(e) => handleChange('allowQueueOutsideHours', e.target.checked)}
          />
          <div className="toggle-content">
            <strong>Tillat kø utenfor åpningstid</strong>
            <span>
              Når aktivert, kan kunder be om eskalering selv om agenter ikke er tilgjengelige.
              Henvendelsen legges i kø til neste åpningstid.
            </span>
          </div>
        </label>
      </div>

      <div className="policy-option">
        <label className="policy-field">
          <span className="field-label">Forsøk før eskalering</span>
          <span className="field-hint">Hvor mange ganger AI-en skal prøve å løse saken før normal eskalering.</span>
          <input
            type="number"
            className="input input-sm"
            value={escalationConfig.maxResolutionAttempts}
            onChange={(e) => handleConfigChange('maxResolutionAttempts', parseInt(e.target.value, 10) || 2)}
            min={1}
            max={10}
          />
        </label>
      </div>

      <div className="policy-option">
        <label className="policy-toggle">
          <input
            type="checkbox"
            checked={escalationConfig.autoEscalateOnFrustration}
            onChange={(e) => handleConfigChange('autoEscalateOnFrustration', e.target.checked)}
          />
          <div className="toggle-content">
            <strong>Eskaler raskt ved frustrasjon eller hast</strong>
            <span>
              Når aktivert, kan AI-en hoppe over forsøksterskelen ved tydelig klage, hast eller sterk frustrasjon.
            </span>
          </div>
        </label>
      </div>

      <div className="policy-option">
        <label className="policy-toggle">
          <input
            type="checkbox"
            checked={escalationConfig.salesEscalationBypassEnabled}
            onChange={(e) => handleConfigChange('salesEscalationBypassEnabled', e.target.checked)}
          />
          <div className="toggle-content">
            <strong>Direkte eskalering ved salgsklare kunder</strong>
            <span>
              Når aktivert, kan AI-en koble inn en person med en gang når kunden virker klar for kjøp eller tilbud.
            </span>
          </div>
        </label>
      </div>

      {policy.allowQueueOutsideHours && (
        <>
          <div className="policy-option">
            <label className="policy-field">
              <span className="field-label">Melding til kunden ved kø</span>
              <textarea
                className="input"
                value={policy.queueMessage}
                onChange={(e) => handleChange('queueMessage', e.target.value)}
                rows={3}
                placeholder="Melding som vises når kunden settes i kø..."
              />
            </label>
          </div>

          <div className="policy-option">
            <label className="policy-field">
              <span className="field-label">Maks køtid (timer)</span>
              <span className="field-hint">Hvor lenge en kø-forespørsel er gyldig</span>
              <input
                type="number"
                className="input input-sm"
                value={policy.maxQueueTimeHours}
                onChange={(e) => handleChange('maxQueueTimeHours', parseInt(e.target.value, 10) || 24)}
                min={1}
                max={168}
              />
            </label>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================
// CURRENT STATUS DISPLAY
// ============================================
const CurrentStatus = ({ mode, schedule }) => {
  // Calculate current slot
  const now = new Date();
  const dayIndex = (now.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
  const hour = now.getHours();
  const currentSlot = getSlotIndex(dayIndex, hour);
  
  let currentMode = mode;
  if (mode === 'combined' && schedule) {
    currentMode = schedule[currentSlot] || 'ai_only';
  }

  const modeConfig = SLOT_MODES[currentMode] || SLOT_MODES.ai_only;
  const dayName = DAYS[dayIndex]?.label || '';

  return (
    <div className="current-status" style={{ '--status-color': modeConfig.color }}>
      <div className="status-indicator">
        <span className="status-dot" />
        <span className="status-mode">{modeConfig.label}</span>
      </div>
      <span className="status-time">
        {dayName} {hour.toString().padStart(2, '0')}:00
      </span>
    </div>
  );
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const BotSchedulesPage = ({ bot: propBot }) => {
  const { selectedCompany, permissions, selectedBot } = useAuth();
  // Use prop bot if provided (when rendered as tab), otherwise fall back to auth context
  const bot = propBot || selectedBot;
  const companyId = selectedCompany?.id ?? null;
  const botId = bot?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Track if initial load is complete - prevents saving before we know the actual state
  const configLoadedRef = useRef(false);

  // Configuration state
  const [supportMode, setSupportMode] = useState('ai_only');
  const [weekSchedule, setWeekSchedule] = useState(createEmptySchedule());
  const [escalationPolicy, setEscalationPolicy] = useState(DEFAULT_ESCALATION_POLICY);
  const [escalationConfig, setEscalationConfig] = useState(DEFAULT_ESCALATION_CONFIG);

  // Calculate current slot for highlighting
  const currentSlot = useMemo(() => {
    const now = new Date();
    const dayIndex = (now.getDay() + 6) % 7;
    const hour = now.getHours();
    return getSlotIndex(dayIndex, hour);
  }, []);

  // Load configuration
  const loadConfig = useCallback(async () => {
    if (!botId) return;
    setLoading(true);
    configLoadedRef.current = false;
    try {
      const botData = await DashboardApi.getBot(botId);
      
      setSupportMode(botData?.support_mode || 'ai_only');
      setWeekSchedule(
        Array.isArray(botData?.week_schedule) && botData.week_schedule.length === 168
          ? botData.week_schedule
          : createEmptySchedule()
      );
      setEscalationPolicy({
        ...DEFAULT_ESCALATION_POLICY,
        ...(botData?.escalation_policy || {}),
      });
      setEscalationConfig({
        ...DEFAULT_ESCALATION_CONFIG,
        ...(botData?.escalation_config || {}),
      });
      setError(null);
      // Mark config as loaded AFTER state is set
      configLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to load config:', err);
      setError('Kunne ikke laste innstillinger');
      notifyError('Kunne ikke laste innstillinger', { source: 'Tidsskjema' });
      // Still allow saves if load fails (user might want to set up fresh config)
      configLoadedRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Clear success message
  useEffect(() => {
    if (success) {
      notifySuccess(success, { source: 'Tidsskjema' });
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Save function - called explicitly when user makes changes
  const saveConfig = useCallback(async (modeOverride, scheduleOverride, policyOverride, escalationConfigOverride) => {
    // Don't save until initial config is loaded
    if (!configLoadedRef.current || !botId) return;
    
    const modeToSave = modeOverride ?? supportMode;
    const scheduleToSave = scheduleOverride ?? weekSchedule;
    const policyToSave = policyOverride ?? escalationPolicy;
    const escalationConfigToSave = escalationConfigOverride ?? escalationConfig;
    
    setSaving(true);
    setError(null);
    try {
      await DashboardApi.updateBotScheduleConfig(botId, {
        supportMode: modeToSave,
        weekSchedule: modeToSave === 'combined' ? scheduleToSave : null,
        escalationPolicy: policyToSave,
        escalationConfig: escalationConfigToSave,
      });
      setSuccess('Lagret');
    } catch (err) {
      console.error('Failed to save:', err);
      const message = err.message || 'Kunne ikke lagre innstillinger';
      setError(message);
      notifyError(message, { source: 'Tidsskjema' });
    } finally {
      setSaving(false);
    }
  }, [botId, supportMode, weekSchedule, escalationPolicy, escalationConfig]);

  // Wrapper functions that save after user actions
  const handleModeChange = (newMode) => {
    if (newMode === supportMode) return; // No change
    setSupportMode(newMode);
    // Save with the new mode explicitly to avoid stale closure issues
    setTimeout(() => saveConfig(newMode), 100);
  };

  // Ref to track latest schedule for saving after paint
  const latestScheduleRef = useRef(weekSchedule);
  
  const handleScheduleChange = (newSchedule) => {
    setWeekSchedule(newSchedule);
    latestScheduleRef.current = newSchedule;
  };

  const handleScheduleSave = () => {
    // Called when user stops painting (mouseup) - use ref for latest value
    saveConfig(undefined, latestScheduleRef.current, undefined);
  };

  const handlePolicyChange = (newPolicy) => {
    setEscalationPolicy(newPolicy);
    // Pass explicit value to avoid stale closure
    setTimeout(() => saveConfig(undefined, undefined, newPolicy), 300);
  };

  const handleEscalationConfigChange = (newConfig) => {
    setEscalationConfig(newConfig);
    setTimeout(() => saveConfig(undefined, undefined, undefined, newConfig), 300);
  };

  // Permission checks
  if (!permissions?.canViewBots) {
    return <Navigate to="/" replace />;
  }

  if (!companyId || !botId) {
    return (
      <div className="page support-page">
        <div className="empty-state-card">
          <span className="empty-icon">🤖</span>
          <h3>Ingen bot valgt</h3>
          <p>Velg en bot fra menyen for å konfigurere support-innstillinger.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page support-page">
      <header className="page-header">
        <div className="header-content">
          <span className="page-eyebrow">Bot: {bot?.name || 'Ukjent'}</span>
          <h1>Tidsskjema</h1>
          <p>Konfigurer når AI-agenten og live-agenter er tilgjengelige.</p>
        </div>
        <div className="header-status">
          {saving && <span className="saving-indicator">Lagrer...</span>}
          <CurrentStatus mode={supportMode} schedule={weekSchedule} />
        </div>
      </header>

      {loading ? (
        <div className="loading-card">
          <div className="loading-spinner" />
          <p>Laster innstillinger...</p>
        </div>
      ) : (
        <>
          {/* Mode Selection */}
          <section className="section mode-section">
            <h2>Tilgjengelighet</h2>
            <div className="mode-cards">
              <ModeCard mode="ai_only" selected={supportMode === 'ai_only'} onClick={handleModeChange} />
              <ModeCard mode="live_only" selected={supportMode === 'live_only'} onClick={handleModeChange} />
              <ModeCard mode="combined" selected={supportMode === 'combined'} onClick={handleModeChange} />
            </div>
          </section>

          {/* Week Calendar (only for combined mode) */}
          {supportMode === 'combined' && (
            <section className="section calendar-section">
              <h2>Ukeoversikt</h2>
              <p className="section-description">
                Definer nøyaktig hvilken modus som skal være aktiv for hver time i uken.
              </p>
              <WeekCalendar
                schedule={weekSchedule}
                onChange={handleScheduleChange}
                onSave={handleScheduleSave}
                currentSlot={currentSlot}
              />
            </section>
          )}

          {/* Escalation Policy (only for combined mode) */}
          {supportMode === 'combined' && (
            <section className="section policy-section">
              <EscalationPolicyPanel
                policy={escalationPolicy}
                onChange={handlePolicyChange}
                escalationConfig={escalationConfig}
                onEscalationConfigChange={handleEscalationConfigChange}
              />
            </section>
          )}

          {/* Simple explanation for non-combined modes */}
          {supportMode !== 'combined' && (
            <section className="section info-section">
              <div className="simple-mode-info">
                {supportMode === 'ai_only' ? (
                  <>
                    <div className="info-icon">🤖</div>
                    <h3>AI-agenten er alltid aktiv</h3>
                    <p>
                      Alle henvendelser håndteres automatisk av AI-agenten, 24 timer i døgnet, 
                      7 dager i uken. Ingen eskalering til menneskelige agenter er tilgjengelig.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="info-icon">👤</div>
                    <h3>Live-agenter håndterer alle henvendelser</h3>
                    <p>
                      Kunder blir koblet direkte til menneskelige agenter. Hvis ingen agenter 
                      er tilgjengelige, tar AI-agenten over som backup.
                    </p>
                  </>
                )}
              </div>
            </section>
          )}

        </>
      )}
    </div>
  );
};

export default BotSchedulesPage;
